import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Mail, Clock, Users, Plus, Trash2, Send, FileText, DollarSign, Home, Wrench, Sparkles, Pencil, ArrowLeft, ToggleLeft, CalendarClock } from "lucide-react";
import { useState, useEffect } from "react";
import AdminLayout from "@/components/layout-admin";

const TIMEZONES = [
  { value: "America/New_York", label: "Eastern (ET)" },
  { value: "America/Chicago", label: "Central (CT)" },
  { value: "America/Denver", label: "Mountain (MT)" },
  { value: "America/Los_Angeles", label: "Pacific (PT)" },
  { value: "America/Anchorage", label: "Alaska (AKT)" },
  { value: "Pacific/Honolulu", label: "Hawaii (HT)" },
  { value: "America/Bogota", label: "Colombia (COT)" },
  { value: "America/Sao_Paulo", label: "Brazil (BRT)" },
  { value: "Europe/London", label: "London (GMT)" },
  { value: "Europe/Madrid", label: "Spain (CET)" },
  { value: "UTC", label: "UTC" },
];

const SEND_TIMES = Array.from({ length: 24 }, (_, h) => {
  const hour = String(h).padStart(2, '0');
  return [
    { value: `${hour}:00`, label: `${h === 0 ? 12 : h > 12 ? h - 12 : h}:00 ${h < 12 ? 'AM' : 'PM'}` },
    { value: `${hour}:30`, label: `${h === 0 ? 12 : h > 12 ? h - 12 : h}:30 ${h < 12 ? 'AM' : 'PM'}` },
  ];
}).flat();

const SECTIONS = [
  { key: "includeRevenue", label: "Revenue Summary", desc: "Today's and month-to-date revenue", icon: DollarSign },
  { key: "includeReservations", label: "Reservations", desc: "Check-ins, check-outs, and active bookings", icon: FileText },
  { key: "includeOccupancy", label: "Occupancy", desc: "Current occupancy rate and unit status", icon: Home },
  { key: "includeCleaningStatus", label: "Cleaning Status", desc: "Pending and completed cleaning jobs", icon: Sparkles },
  { key: "includeMaintenanceTickets", label: "Maintenance Tickets", desc: "Open maintenance tickets count", icon: Wrench },
];

function defaultForm() {
  return {
    name: "Daily Report",
    enabled: true,
    sendTime: "18:00",
    timezone: "America/New_York",
    recipients: [] as Array<{ email: string; name: string }>,
    includeRevenue: true,
    includeOccupancy: true,
    includeCleaningStatus: true,
    includeMaintenanceTickets: true,
    includeReservations: true,
  };
}

export default function AdminDailyReports() {
  const { toast } = useToast();
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formState, setFormState] = useState<any>(null);
  const [newEmail, setNewEmail] = useState("");
  const [newName, setNewName] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<number | null>(null);

  const { data: configs = [], isLoading } = useQuery<any[]>({ queryKey: ['/api/admin/daily-report-settings'] });
  const { data: teamUsers } = useQuery<any[]>({ queryKey: ['/api/admin/users'] });

  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await apiRequest("POST", "/api/admin/daily-report-settings", data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/daily-report-settings'] });
      toast({ title: "Report created", description: "New report configuration has been created." });
      setFormState(null);
      setEditingId(null);
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: any }) => {
      const res = await apiRequest("PUT", `/api/admin/daily-report-settings/${id}`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/daily-report-settings'] });
      toast({ title: "Report updated", description: "Report configuration has been saved." });
      setFormState(null);
      setEditingId(null);
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await apiRequest("DELETE", `/api/admin/daily-report-settings/${id}`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/daily-report-settings'] });
      toast({ title: "Report deleted" });
      setShowDeleteConfirm(null);
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const sendNowMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await apiRequest("POST", `/api/admin/daily-report/send-now/${id}`);
      return res.json();
    },
    onSuccess: (data: any) => {
      toast({ title: "Report sent", description: `Sent to ${data.sentTo} recipient(s).` });
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const startNew = () => {
    setEditingId(null);
    setFormState(defaultForm());
  };

  const startEdit = (config: any) => {
    setEditingId(config.id);
    setFormState({
      name: config.name || "Daily Report",
      enabled: config.enabled ?? true,
      sendTime: config.sendTime ?? "18:00",
      timezone: config.timezone ?? "America/New_York",
      recipients: config.recipients ?? [],
      includeRevenue: config.includeRevenue ?? true,
      includeOccupancy: config.includeOccupancy ?? true,
      includeCleaningStatus: config.includeCleaningStatus ?? true,
      includeMaintenanceTickets: config.includeMaintenanceTickets ?? true,
      includeReservations: config.includeReservations ?? true,
    });
  };

  const handleSave = () => {
    if (!formState.name?.trim()) {
      toast({ title: "Name required", description: "Please give this report a name.", variant: "destructive" });
      return;
    }
    if (editingId) {
      updateMutation.mutate({ id: editingId, data: formState });
    } else {
      createMutation.mutate(formState);
    }
  };

  const addRecipient = () => {
    if (!newEmail.trim()) return;
    const exists = formState.recipients.some((r: any) => r.email === newEmail.trim());
    if (exists) { toast({ title: "Already added", variant: "destructive" }); return; }
    setFormState({ ...formState, recipients: [...formState.recipients, { email: newEmail.trim(), name: newName.trim() || newEmail.trim().split('@')[0] }] });
    setNewEmail("");
    setNewName("");
  };

  const addTeamMember = (user: any) => {
    if (!user.email) { toast({ title: "No email", description: `${user.fullName} doesn't have an email address.`, variant: "destructive" }); return; }
    const exists = formState.recipients.some((r: any) => r.email === user.email);
    if (exists) { toast({ title: "Already added", variant: "destructive" }); return; }
    setFormState({ ...formState, recipients: [...formState.recipients, { email: user.email, name: user.fullName || user.username }] });
  };

  const removeRecipient = (email: string) => {
    setFormState({ ...formState, recipients: formState.recipients.filter((r: any) => r.email !== email) });
  };

  const getTimeLabel = (val: string) => SEND_TIMES.find(t => t.value === val)?.label || val;
  const getTzLabel = (val: string) => TIMEZONES.find(t => t.value === val)?.label || val;
  const enabledSections = (config: any) => SECTIONS.filter(s => config[s.key] !== false).map(s => s.label);

  if (isLoading) return <AdminLayout><div className="p-8 text-center text-muted-foreground">Loading...</div></AdminLayout>;

  if (formState) {
    return (
      <AdminLayout>
        <div className="space-y-6 p-4 md:p-6 max-w-3xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => { setFormState(null); setEditingId(null); }} data-testid="btn-back">
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold tracking-tight" data-testid="page-title-edit-report">
                  {editingId ? "Edit Report" : "New Report"}
                </h1>
                <p className="text-muted-foreground mt-0.5 text-sm">Configure who gets this report and what it includes</p>
              </div>
            </div>
            <div className="flex gap-2">
              {editingId && (
                <Button
                  variant="outline"
                  onClick={() => sendNowMutation.mutate(editingId)}
                  disabled={sendNowMutation.isPending || formState.recipients.length === 0}
                  data-testid="btn-send-now"
                >
                  <Send className="h-4 w-4 mr-2" />
                  {sendNowMutation.isPending ? "Sending..." : "Send Now"}
                </Button>
              )}
              <Button
                className="bg-[#C2652A] hover:bg-[#A8531E]"
                onClick={handleSave}
                disabled={updateMutation.isPending || createMutation.isPending}
                data-testid="btn-save-settings"
              >
                {(updateMutation.isPending || createMutation.isPending) ? "Saving..." : "Save"}
              </Button>
            </div>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Report Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Report Name</Label>
                <Input
                  value={formState.name}
                  onChange={(e) => setFormState({ ...formState, name: e.target.value })}
                  placeholder="e.g. Cleaning Team Report, Finance Summary"
                  data-testid="input-report-name"
                />
              </div>
              <div className="flex items-center justify-between pt-2">
                <Label>Enabled</Label>
                <Switch
                  checked={formState.enabled}
                  onCheckedChange={(v) => setFormState({ ...formState, enabled: v })}
                  data-testid="switch-enabled"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2"><Clock className="h-4 w-4" /> Send Time</Label>
                  <Select value={formState.sendTime} onValueChange={(v) => setFormState({ ...formState, sendTime: v })}>
                    <SelectTrigger data-testid="select-send-time"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {SEND_TIMES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Timezone</Label>
                  <Select value={formState.timezone} onValueChange={(v) => setFormState({ ...formState, timezone: v })}>
                    <SelectTrigger data-testid="select-timezone"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TIMEZONES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2"><Users className="h-5 w-5" /> Recipients</CardTitle>
              <CardDescription>Who receives this report</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {formState.recipients.length > 0 && (
                <div className="space-y-2">
                  {formState.recipients.map((r: any) => (
                    <div key={r.email} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg" data-testid={`recipient-${r.email}`}>
                      <div className="flex items-center gap-3">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="text-sm font-medium">{r.name}</p>
                          <p className="text-xs text-muted-foreground">{r.email}</p>
                        </div>
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => removeRecipient(r.email)} data-testid={`remove-recipient-${r.email}`}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              <div className="border-t pt-4 space-y-3">
                <p className="text-sm font-medium">Add from team</p>
                <div className="flex flex-wrap gap-2">
                  {teamUsers?.filter((u: any) => u.email && !formState.recipients.some((r: any) => r.email === u.email)).slice(0, 10).map((u: any) => (
                    <Button key={u.id} variant="outline" size="sm" onClick={() => addTeamMember(u)} data-testid={`add-team-${u.id}`}>
                      <Plus className="h-3 w-3 mr-1" /> {u.fullName || u.username}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="border-t pt-4 space-y-3">
                <p className="text-sm font-medium">Add custom email</p>
                <div className="flex gap-2">
                  <Input placeholder="Name" value={newName} onChange={(e) => setNewName(e.target.value)} className="max-w-[180px]" data-testid="input-new-name" />
                  <Input placeholder="Email" type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} className="flex-1" data-testid="input-new-email" />
                  <Button variant="outline" onClick={addRecipient} data-testid="btn-add-recipient">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2"><Sparkles className="h-5 w-5" /> Report Sections</CardTitle>
              <CardDescription>Choose what data to include</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {SECTIONS.map(item => (
                  <div key={item.key} className="flex items-center justify-between p-3 rounded-lg hover:bg-muted/50 transition-colors">
                    <div className="flex items-center gap-3">
                      <item.icon className="h-4 w-4 text-[#C2652A]" />
                      <div>
                        <p className="text-sm font-medium">{item.label}</p>
                        <p className="text-xs text-muted-foreground">{item.desc}</p>
                      </div>
                    </div>
                    <Switch
                      checked={formState[item.key]}
                      onCheckedChange={(v) => setFormState({ ...formState, [item.key]: v })}
                      data-testid={`switch-${item.key}`}
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6 p-4 md:p-6 max-w-3xl mx-auto">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2" data-testid="page-title-daily-reports">
              <FileText className="h-6 w-6 text-[#C2652A]" /> Daily Reports
            </h1>
            <p className="text-muted-foreground mt-1">Create different reports for different teams</p>
          </div>
          <Button className="bg-[#C2652A] hover:bg-[#A8531E]" onClick={startNew} data-testid="btn-new-report">
            <Plus className="h-4 w-4 mr-2" /> New Report
          </Button>
        </div>

        {configs.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12 text-center">
              <FileText className="h-12 w-12 text-muted-foreground/40 mb-4" />
              <h3 className="text-lg font-medium mb-1">No reports configured</h3>
              <p className="text-sm text-muted-foreground mb-4">Create your first automated daily report to keep your team informed.</p>
              <Button className="bg-[#C2652A] hover:bg-[#A8531E]" onClick={startNew} data-testid="btn-new-report-empty">
                <Plus className="h-4 w-4 mr-2" /> Create Report
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {configs.map((config: any) => (
              <Card key={config.id} className="hover:shadow-md transition-shadow" data-testid={`report-card-${config.id}`}>
                <CardContent className="p-4 md:p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-base truncate" data-testid={`report-name-${config.id}`}>{config.name || "Daily Report"}</h3>
                        <Badge variant={config.enabled ? "default" : "secondary"} className={config.enabled ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200" : ""}>
                          {config.enabled ? "Active" : "Paused"}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground mt-1">
                        <span className="flex items-center gap-1">
                          <CalendarClock className="h-3.5 w-3.5" />
                          {getTimeLabel(config.sendTime)} ({getTzLabel(config.timezone)})
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="h-3.5 w-3.5" />
                          {(config.recipients as any[])?.length || 0} recipient{(config.recipients as any[])?.length === 1 ? '' : 's'}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {enabledSections(config).map(s => (
                          <Badge key={s} variant="outline" className="text-xs font-normal">{s}</Badge>
                        ))}
                      </div>
                      {(config.recipients as any[])?.length > 0 && (
                        <p className="text-xs text-muted-foreground mt-2 truncate">
                          To: {(config.recipients as any[]).map((r: any) => r.name || r.email).join(", ")}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => sendNowMutation.mutate(config.id)}
                        disabled={sendNowMutation.isPending || !(config.recipients as any[])?.length}
                        data-testid={`btn-send-now-${config.id}`}
                      >
                        <Send className="h-3.5 w-3.5 mr-1" />
                        Send
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => startEdit(config)} data-testid={`btn-edit-${config.id}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => setShowDeleteConfirm(config.id)} data-testid={`btn-delete-${config.id}`}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Dialog open={showDeleteConfirm !== null} onOpenChange={() => setShowDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Report</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">Are you sure you want to delete this report configuration? This cannot be undone.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => showDeleteConfirm && deleteMutation.mutate(showDeleteConfirm)} disabled={deleteMutation.isPending} data-testid="btn-confirm-delete">
              {deleteMutation.isPending ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
